import { CONTACTS } from 'constants/contactInfo';
import { APP_ANDROID, APP_IPHONE } from 'constants/routes';

import { getApolloContext } from '@apollo/client';
import useMyReferralStats from 'hooks/useMyReferralStats';
import { isLoggedInSelector } from 'modules/auth';
import React from 'react';
import { useSelector } from 'react-redux';

import { ReactComponent as BtnAppStore } from '@skippay/core/assets/buttons/app-store-button-white.svg';
import { ReactComponent as BtnGooglePlay } from '@skippay/core/assets/buttons/google-play-button-white.svg';
import { ReactComponent as MailIcon } from '@skippay/core/assets/icons/email.svg';
import { ReactComponent as CsobWhite } from '@skippay/core/assets/logos/csob.svg';
import { ReactComponent as BtnFacebook } from '@skippay/core/assets/logos/facebook.svg';
import { ReactComponent as BtnWhatsApp } from '@skippay/core/assets/logos/whatsapp.svg';
import Container from '@skippay/core/components/Container';
import Link from '@skippay/core/components/Link';
import Step from '@skippay/core/components/Step';
import TextPublic from '@skippay/core/components/TextPublic';

import NavigationList from '../NavigationList';
import { SButton } from './index.styled';
import useFooterLinks from './useLinks';

import './index.sass';
import useInitialCountryLocale from "@skippay/core/hooks/useInitialCountryLocale";
import { FormattedMessage } from '@skippay/core/i18n/intl';
import { be, bm } from '@skippay/core/utils/classUtils';

const MODULE_NAME = 'Footer';

const Title = ({ children }) => (
	<div className={be(MODULE_NAME, 'title')}>
		<TextPublic component="h2" strong className="fontSecondary" lg colorInverted>
			{children}
		</TextPublic>
	</div>
);

function ReferralShare() {
	const { referralUrl } = useMyReferralStats();

	if (!referralUrl) {
		return null;
	}

	const encodedReferralLink = encodeURIComponent(referralUrl);

	return (
		<div className={be(MODULE_NAME, 'section')}>
			<Title>
				<FormattedMessage
					id="Footer.referral"
					defaultMessage="Doporučte nás a získejte až 1 000 Kč"
				/>
			</Title>

			<TextPublic component="p" colorInverted>
				<FormattedMessage id="Footer.sendInvitation" defaultMessage="Pošlete pozvánku" />
			</TextPublic>

			<Step v xs />

			<SButton
				variant="info"
				noBorder
				testId="messenger"
				analyticsId="messenger"
				to={`https://www.facebook.com/sharer/sharer.php?u=${encodedReferralLink}`}
				width="auto"
			>
				<span className="u-top--2">
					<BtnFacebook />
				</span>
				<Step h xs smaller />
				<FormattedMessage id="Footer.fb" defaultMessage="Facebook" />
			</SButton>
			<Step h xs smaller />
			<SButton
				variant="info"
				noBorder
				testId="whatsapp"
				analyticsId="whatsapp"
				to={`https://wa.me/?text=${encodedReferralLink}`}
				width="auto"
			>
				<span className="u-top--2">
					<BtnWhatsApp />
				</span>
				<Step h xs smaller />
				<FormattedMessage id="Footer.wa" defaultMessage="WhatsApp" />
			</SButton>
		</div>
	);
}

/**
 * LayoutPublic is used for ErrorScreen, where ApolloProvider is not set, this wrapper tests existence of apolloContext
 * before it renders <ReferralShare />
 */
const SafeReferralShare = () => {
	const { Consumer } = getApolloContext();

	return <Consumer>{(context) => (context?.client ? <ReferralShare /> : null)}</Consumer>;
};

function Footer(): JSX.Element {
	const isLoggedIn = useSelector(isLoggedInSelector);
	const year = new Date().getFullYear();
	const links = useFooterLinks();
	const { locale } = useInitialCountryLocale();
	const contactEmail = CONTACTS(locale).email;

	return (
		<footer className={bm(MODULE_NAME, 'hidden-print')} data-clarity-unmask="true">
			<div className={be(MODULE_NAME, 'content')}>
				<Container xl>
					<div className="grid gap-6 sm:grid-cols-2 md:grid-cols-12">
						<div className="sm:col-span-1 md:col-span-3">
							<div className={be(MODULE_NAME, 'section')}>
								<Title>
									<FormattedMessage id="Footer.whereNext" defaultMessage="Kam dál?" />
								</Title>
								<NavigationList links={links.linksWhereNext} />
							</div>
						</div>
						<div className="sm:col-span-1 md:col-span-3">
							<div className={be(MODULE_NAME, 'section')}>
								<Title>
									<FormattedMessage id="Footer.otherInformation" defaultMessage="Další informace" />
								</Title>
								<NavigationList links={links.linksInformations} />
							</div>
						</div>
						<div className="sm:col-span-1 md:col-span-4">
							<div className={be(MODULE_NAME, 'section')}>
								<Title>
									<FormattedMessage id="Footer.bePartners" defaultMessage="Buďme partneři" />
								</Title>
								<NavigationList links={links.linksForEshops} />
							</div>
							<div className={be(MODULE_NAME, 'section')}>
								<Title>
									<FormattedMessage id="Footer.contact" defaultMessage="Ozvěte se nám" />
								</Title>
								<Link to={`mailto:${contactEmail}`} testId="email" analyticsId="email" variant='inverted' textDecoration={['none', 'underline']}>
									<MailIcon />
									<Step h xs />
									{contactEmail}
								</Link>
							</div>
						</div>
						<div className="sm:col-span-1 md:col-span-2">
							<div className={be(MODULE_NAME, 'section')}>
								<Title>
									<FormattedMessage id="Footer.app" defaultMessage="Stáhněte si aplikaci" />
								</Title>
								<Link testId="app-store" analyticsId="appStore" to={APP_IPHONE} withImage aria-label="App Store">
									<BtnAppStore />
								</Link>

								<Step v xs smaller />

								<Link testId="google-play" analyticsId="googlePlay" to={APP_ANDROID} withImage aria-label="Google Play">
									<BtnGooglePlay />
								</Link>
							</div>
							<div className={be(MODULE_NAME, 'section')}>
								<Title>
									<FormattedMessage id="Footer.social" defaultMessage="Sledujte nás" />
								</Title>
								<NavigationList links={links.linksSocial} onRow verticalCenter />
							</div>
						</div>
						<div className="md:col-span-6">
							<div className={be(MODULE_NAME, 'section')}>
								<Title>
									<FormattedMessage id="Footer.seo" defaultMessage="Mohlo by vás zajímat" />
								</Title>
								<NavigationList links={links.seoLinks} onRow />
							</div>
						</div>
						<div className="md:col-span-4" data-clarity-mask="true">
							{isLoggedIn && <SafeReferralShare />}
						</div>
						<div className="md:col-span-2">
							<div className={be(MODULE_NAME, 'section')}>
								<Title>
									<FormattedMessage id="Footer.csob" defaultMessage="Ve&nbsp;spolupráci&nbsp;s" />
								</Title>
								<Link withImage to="https://www.csob.cz/" testId="csob" analyticsId="csob" aria-label="ČSOB">
									<div className={be(MODULE_NAME, 'csob')}>
										<CsobWhite />
									</div>
								</Link>
							</div>
						</div>
					</div>
				</Container>
			</div>
			<div className={be(MODULE_NAME, 'copy')}>
				<Container xl>
					<TextPublic component="p" colorInverted>
						<span>©{year}. </span>
						<FormattedMessage
							id="Footer.copy"
							defaultMessage="Skip Pay. Provozovatel služby je Skip Pay s.r.o."
						/>
					</TextPublic>
				</Container>
			</div>
		</footer>
	);
}

export default Footer;
